import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import {
  CostCenterAssignment,
  FormattedAssignment,
} from 'modules/finance/lib/costCenterAssignments'

import {
  hasUnassignedCostCenter,
  hasZeroDedication,
  validDedication,
} from '../../../lib/dedication'
import { canBeMarkedAsActive } from './validations/canBeMarkedAsActive'
import { DateRange, assignmentOverlaps } from './validations/overlaps'

interface Props {
  selectedDateRange: DateRange
  currentAssignments: FormattedAssignment[]
  displayedAssignments: FormattedAssignment[]
  employeeAssignments: CostCenterAssignment[]
  userInteracted: boolean
}

export const useAssignmentsValidator = ({
  selectedDateRange,
  displayedAssignments,
  currentAssignments,
  employeeAssignments,
  userInteracted,
}: Props) => {
  const defaultAssignmentsIds = currentAssignments.map((assignment) => assignment.id)
  const isCurrentActive =
    currentAssignments.length > 0 && currentAssignments.every((i) => i.endDate === undefined)

  const employeeAssignmentsExcludingCurrent = employeeAssignments.filter(
    (assignment) => !defaultAssignmentsIds.includes(String(assignment.id))
  )

  const assignedDates = useUnsafeMemo(() => {
    return employeeAssignmentsExcludingCurrent.map((v) => ({
      startDate: v.startDate,
      endDate: v.endDate || undefined,
    }))
  }, [employeeAssignmentsExcludingCurrent])

  const overlappedAssignments = useUnsafeMemo(() => {
    return assignmentOverlaps({
      assignedDates,
      selectedDateRange,
    })
  }, [assignedDates, selectedDateRange])

  const assignmentCanBeMarkedAsActive = useUnsafeMemo(() => {
    return canBeMarkedAsActive({
      employeeAssignments,
      currentAssignments,
      isCurrentActive,
      startDate: selectedDateRange.startDate,
    })
  }, [employeeAssignments, currentAssignments, isCurrentActive, selectedDateRange])

  const validations = useUnsafeMemo(() => {
    return {
      overlappedAssignments: overlappedAssignments,
      validDedication: validDedication(displayedAssignments),
      hasZeroDedication: hasZeroDedication(displayedAssignments),
      hasUnassignedCostCenter: hasUnassignedCostCenter(displayedAssignments),
      canBeMarkedAsActive: assignmentCanBeMarkedAsActive,
    }
  }, [displayedAssignments, overlappedAssignments, assignmentCanBeMarkedAsActive])

  const validForSave = useUnsafeMemo(() => {
    return (
      userInteracted &&
      validations.validDedication &&
      !validations.hasZeroDedication &&
      !validations.hasUnassignedCostCenter &&
      !validations.overlappedAssignments &&
      selectedDateRange.startDate != ''
    )
  }, [selectedDateRange, validations, userInteracted])

  return {
    validForSave,
    validations,
  }
}
